import { toast } from "sonner";
import { claimSessionTransition } from "./sessionExpiry";

/**
 * The one way this app ends a session (issues #262, #318, #326).
 *
 * Three controls sign out — the topbar account menu, the Settings row, and
 * the sidebar button — and before this they were three hand-rolled
 * `await logout(); nav(...)` pairs that had already drifted to two different
 * destinations. One function, one destination.
 *
 * WHY A FULL PAGE LOAD, NOT `useNavigate`. `logout()` sets the context user
 * to `false`, and the instant it does, `ProtectedRoute` renders
 * `<Navigate to="/login" replace />` for whatever page you were on. A
 * client-side `nav("/")` on the next line is therefore a RACE between two
 * redirects, and it loses: measured, the app landed on `/login` every time.
 * Rather than fight the router's own (correct) behaviour with timing, this
 * leaves the SPA entirely.
 *
 * That is also the stronger answer on its own merits. A reload guarantees
 * nothing from the previous session survives in memory — no fetched roster,
 * no half-rendered page, no context still holding the last user — which is
 * exactly the promise "sign out" makes on the shared phone #262 is about.
 * The cost is one page load at the moment a user has explicitly said they
 * are done.
 *
 * WHY "/" AND NOT "/login". #262's title is "the front door is
 * unreachable": the session cookie lasts 24h, so every return visit to
 * `/` bounced to `/dashboard` and the page that explains the product could
 * not be reached again by anyone who had ever signed in on that device.
 * Signing out is the moment to give it back. `Landing` still forwards a
 * signed-in visitor on to `/dashboard`, so this can only ever be the
 * landing page for someone who is now genuinely anonymous.
 *
 * ISSUE #326 — WHY IT NOW HAS A FAILURE BRANCH. This used to be two
 * unconditional lines: `await logout(); window.location.assign("/")`. And
 * `logout()` swallowed any transport failure with a `console.debug` before
 * clearing the in-memory user, so a `POST /auth/logout` that 5xx'd or never
 * left the device produced EXACTLY the same screen as one that succeeded:
 * the anonymous landing page, on a device whose httpOnly session cookie was
 * still live and still good for the rest of its 24 hours. The next person to
 * pick up that shared phone and open the app is silently signed in as the
 * person who thought they had signed out — #262's own threat model, reached
 * by a route #262's fix did not close.
 *
 * The rule this encodes: the app may not navigate on a claim it has not
 * earned. A sign-out that did not happen must look like a sign-out that did
 * not happen — same page, same session, an error saying so — because the
 * only recovery available to the user (press it again, or don't walk away
 * from the phone) requires knowing.
 */

/**
 * "The server told us this cookie is worthless" — which is a SUCCESSFUL
 * sign-out, arriving as an error status.
 *
 * `POST /api/auth/logout` sits behind `requireUser` (api/src/routes/auth.ts),
 * so a request carrying no cookie, an unknown token, or an expired session
 * is answered 401 rather than 200. In every one of those cases there is no
 * live session left to end: whatever the browser is still holding cannot
 * authenticate anything. Treating that as a failure would strand a user on
 * "we couldn't sign you out" while they are, in fact, signed out — and
 * teach them to ignore the warning that matters.
 *
 * Nothing else qualifies. A 5xx, a 429, a timeout (#336) or a dropped
 * connection all mean the same thing: we do not know whether the session row
 * was deleted, so we must assume it was not.
 */
export function sessionAlreadyEnded(error) {
  return error?.response?.status === 401;
}

export const SIGN_OUT_FAILED_MESSAGE =
  "We couldn't sign you out — you are still signed in on this device. Check your connection and try again.";

/**
 * Runs `logout()` and navigates home only if the session really ended.
 *
 * `logout` is `AuthContext`'s, and returns `{ ok, error }` (#326). The
 * `result?.ok === false` shape rather than `!result?.ok` is deliberate: a
 * caller that hands in some other `logout` returning nothing keeps the old
 * behaviour instead of becoming permanently unable to sign out.
 *
 * Returns the result so a caller with its own busy state can stop spinning.
 *
 * `silent` (issue #975, D57/D186.7) — opt-in, default `false`, which keeps
 * every existing caller (the account menu, the shipped Settings row)
 * toasting exactly as before. Settings A's own sign-out control shows a
 * failure ON THE PAGE instead (`settings-signout-error`, D57: a refusal
 * stays on the page, never a toast) — passing `silent: true` there
 * suppresses this function's own `toast.error` so the caller's on-page
 * message is the only one shown, rather than the toast AND the page
 * disagreeing about how a failure looks.
 */
export async function signOutAndGoHome(logout, { silent = false } = {}) {
  // Issue #396. The 401 interceptor added there would otherwise steal this
  // destination: the moment the server deletes the session row, every request
  // this page still has in flight comes back 401 — a dashboard mount alone
  // produces ten of them — and the first one would send the user to
  // `/login?session=expired`. That is the wrong screen and the wrong sentence
  // for somebody who just pressed "Sign out", and it walks back #262's whole
  // point (the landing page has to be reachable again). Claimed before the
  // request, because the gap is between the server deleting the row and this
  // line hearing about it.
  const releaseSessionTransition = claimSessionTransition();
  const result = await logout();
  if (result?.ok === false) {
    // The session is still alive, so hand the latch back: a real expiry
    // later in this same page load must still be able to act on its 401.
    releaseSessionTransition();
    if (!silent) toast.error(result.error || SIGN_OUT_FAILED_MESSAGE);
    return result;
  }
  window.location.assign("/");
  return { ok: true, error: "" };
}

/**
 * Issue #515: "Sign out everywhere" — the Settings control that revokes
 * every session the account holds, on every device, this one included.
 *
 * Same shape as `signOutAndGoHome` above, deliberately (latch first, act,
 * release-and-report on failure, full page load on success) and for the
 * same reasons — its docstring is this function's too. Two differences,
 * both meant:
 *
 *   - `logoutEverywhere` (AuthContext) instead of `logout`: one POST to
 *     `/auth/sign-out-everywhere`, which deletes every session row for the
 *     account, not just this device's.
 *   - It lands on `/login`, not `/`. Ordinary sign-out goes to the front
 *     door because #262 is about giving the landing page back; this
 *     control's user is mid-task on their ACCOUNT's security — they just
 *     revoked every device, usually to sign straight back in on this one —
 *     and the sign-in form is that task's next step, not the product
 *     pitch. A hardcoded literal (no query, nothing interpolated), so no
 *     open-redirect surface — the same stance as
 *     `sessionExpiry.js`'s `endSessionAndSignIn`.
 *
 * `silent` — same opt-in as `signOutAndGoHome` above, same reason: Settings
 * A's sign-out-everywhere control shows a failure on the page instead.
 */
export async function signOutEverywhereAndGoToSignIn(logoutEverywhere, { silent = false } = {}) {
  const releaseSessionTransition = claimSessionTransition();
  const result = await logoutEverywhere();
  if (result?.ok === false) {
    releaseSessionTransition();
    if (!silent) toast.error(result.error || SIGN_OUT_FAILED_MESSAGE);
    return result;
  }
  window.location.assign("/login");
  return { ok: true, error: "" };
}
